<template>
  <div class="month-end-container">
    <!-- SIDEBAR -->
    <Sidebar @open-contacts="goToEmailContacts" />

    <!-- MAIN -->
    <main class="main">
      <!-- TOPBAR -->
      <Topbar />

      <!-- CONTENT -->
      <div class="content">
        <!-- Period Selector -->
        <div class="page-controls">
          <div class="period-selector">
            <select v-model="selectedMonth" @change="fetchReports" class="period-select">
              <option v-for="m in months" :key="m.value" :value="m.value">
                {{ m.label }}
              </option>
            </select>
            <select v-model="selectedYear" @change="fetchReports" class="period-select">
              <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
        </div>
    <!-- 3-stage Progress Card (Refonte W1 #8) -->
    <div class="progress-card-v2">
      <div class="progress-header-v2">
        <div class="progress-title-v2">{{ months.find(m => m.value === selectedMonth)?.label }} {{ selectedYear }} progress</div>
        <div class="properties-managed">{{ stats.total }} properties managed</div>
      </div>

      <div class="stages-grid">
        <div class="stage-card" :class="{ active: activeStage === 1, done: needsReviewCount === 0 && stats.total > 0 }">
          <div class="stage-circle">1</div>
          <div class="stage-body">
            <div class="stage-name">Review &amp; approve</div>
            <div class="stage-detail">{{ reviewedCount }} of {{ stats.total }} reviewed</div>
          </div>
        </div>
        <div class="stage-card" :class="{ active: activeStage === 2, done: stats.sent === stats.total && stats.total > 0 }">
          <div class="stage-circle">2</div>
          <div class="stage-body">
            <div class="stage-name">Send to owners</div>
            <div class="stage-detail">{{ stats.sent }} of {{ stats.total }} sent</div>
          </div>
        </div>
        <div class="stage-card" :class="{ active: activeStage === 3, done: isComplete }">
          <div class="stage-circle">3</div>
          <div class="stage-body">
            <div class="stage-name">Complete</div>
            <div class="stage-detail">{{ isComplete ? 'All sent &amp; done' : 'Not yet' }}</div>
          </div>
        </div>
      </div>

      <div class="progress-bars-stack">
        <div class="progress-row">
          <div class="progress-row-label">Reviewed &amp; approved</div>
          <div class="progress-row-count">{{ reviewedCount }} of {{ stats.total }}</div>
        </div>
        <div class="progress-bar-v2 bar-violet">
          <div class="progress-bar-v2-fill" :style="{ width: reviewedPercent + '%' }"></div>
        </div>
        <div class="progress-row">
          <div class="progress-row-label">Sent to owners</div>
          <div class="progress-row-count">{{ stats.sent }} of {{ stats.total }}</div>
        </div>
        <div class="progress-bar-v2 bar-green">
          <div class="progress-bar-v2-fill" :style="{ width: sentPercent + '%' }"></div>
        </div>
      </div>

      <div class="progress-subtitle">{{ subtitleText }}</div>
    </div>

    <!-- Ready to send banner -->
    <div v-if="readyToSendCount > 0" class="ready-banner">
      <div class="ready-banner-text">
        <div class="ready-banner-title">{{ readyToSendCount }} report{{ readyToSendCount === 1 ? '' : 's' }} reviewed and ready to send.</div>
        <div class="ready-banner-sub">Send individually below or use "Send all ready" above.</div>
      </div>
      <button
        @click="sendAllUnsent"
        class="btn-send-all-ready"
        :disabled="sending"
      >
        Send all ready ({{ readyToSendCount }})
      </button>
    </div>

    <!-- Push to Xero (kept) -->
    <div class="xero-action-row" v-if="stats.sent > 0">
      <button
        @click="openXeroConfirmModal"
        class="btn-push-xero"
        :disabled="pushing"
      >
        Push to Xero
      </button>
    </div>

    <!-- Properties Table Card -->
    <div class="table-card-modern">
      <div class="properties-header">
        <div class="properties-title">Properties</div>
        <div class="filters-pills-v2">
          <button @click="changeFilter('all')" :class="{ active: currentFilter === 'all' }" class="pill-btn-v2">All</button>
          <button @click="changeFilter('needs_action')" :class="{ active: currentFilter === 'needs_action' }" class="pill-btn-v2">Needs review</button>
          <button @click="changeFilter('ready_to_send')" :class="{ active: currentFilter === 'ready_to_send' }" class="pill-btn-v2">Ready to send</button>
          <button @click="changeFilter('sent')" :class="{ active: currentFilter === 'sent' }" class="pill-btn-v2">Sent</button>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading reports...</p>
      </div>

      <table v-else-if="reports.length > 0" class="reports-table-modern">
        <thead>
          <tr>
            <th class="th-property">PROPERTY</th>
            <th class="th-right">SETTLEMENT</th>
            <th class="th-right">COMMISSION</th>
            <th class="th-status">STATUS</th>
            <th class="th-action">ACTION</th>
            <th class="th-unlock">UNLOCK</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="report in reports" :key="report.id">
            <td>
              <div class="property-info">
                <div class="property-name">{{ report.property_name }}</div>
                <div class="owner-name-gray">{{ report.owner_name || 'N/A' }}</div>
              </div>
            </td>
            <td class="td-right">
              <div class="settlement-amount">£{{ formatGBP(report.settlement_amount) }}</div>
            </td>
            <td class="td-right">
              <div class="commission-amount">£{{ formatGBP(report.management_fee) }}</div>
            </td>
            <td class="td-status">
              <span
                class="status-badge"
                :class="getStatusBadgeClass(report.status)"
              >
                {{ getStatusLabel(report.status) }}
              </span>
            </td>
            <td class="td-action">
              <div class="action-buttons">
                <button
                  v-if="report.status === 'ready_to_send'"
                  @click="openSendModal(report)"
                  class="btn-send-action"
                  :disabled="sending"
                >
                  Send
                </button>
                <span
                  v-else-if="report.status === 'sent' || report.status === 'pushed_to_xero'"
                  class="checkmark-icon"
                  title="Sent"
                >✓</span>
                <template v-else>
                  <button
                    @click="$router.push('/reservations')"
                    class="btn-review-action"
                  >
                    Review
                  </button>
                  <button
                    @click="openDeleteModal(report)"
                    class="btn-delete-action"
                    :disabled="deleting"
                  >
                    🗑 Delete
                  </button>
                </template>
              </div>
            </td>
            <td class="td-unlock">
              <button
                v-if="['ready_to_send', 'sent', 'pushed_to_xero'].includes(report.status)"
                @click="openUnlockModal(report)"
                class="btn-unlock-icon"
                :disabled="unlocking"
                title="Unlock"
              >
                <svg viewBox="0 0 14 14" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8">
                  <rect x="2" y="6" width="10" height="7" rx="1"/>
                  <path d="M4 6V4a3 3 0 0 1 6 0v2" stroke-linecap="round"/>
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else class="empty-state">
        <p>No reports found for {{ months.find(m => m.value === selectedMonth)?.label }} {{ selectedYear }}</p>
        <router-link to="/reservations" class="link-primary">Go to Reservations to create reports</router-link>
      </div>
    </div>

    <!-- Modal Send Report -->
    <div v-if="showSendModal" class="modal-overlay" @click.self="closeSendModal">
      <div class="modal-send-report">
        <div class="modal-header-gradient">
          <div class="modal-icon-circle">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <path d="M2 8l10 6 10-6"/>
            </svg>
          </div>
          <h3 class="modal-title">Send report by email</h3>
          <p class="modal-subtitle">{{ selectedReport?.property_name }} — {{ months.find(m => m.value === selectedMonth)?.label }} {{ selectedYear }}</p>
        </div>

        <div class="modal-body">
          <div class="sending-to-section">
            <div class="sending-to-label">SENDING TO</div>
            <div class="recipient-card">
              <div class="avatar-circle">{{ getInitials(selectedReport?.owner_name) }}</div>
              <div class="recipient-info">
                <div class="recipient-name">{{ selectedReport?.owner_name }}</div>
                <div class="recipient-email">{{ selectedReport?.owner_email }}</div>
              </div>
            </div>
          </div>

          <div class="warning-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>This report will be sent immediately to the owner's email address</span>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeSendModal" class="btn-modal-cancel">Cancel</button>
          <button @click="confirmSendReport" class="btn-modal-send" :disabled="sending">
            <span v-if="!sending">Send report</span>
            <span v-else>Sending...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Success Xero -->
    <div v-if="showSuccessModal" class="modal-overlay" @click.self="showSuccessModal = false">
      <div class="modal-success">
        <div class="modal-header-success">
          <div class="checkmark-circle">
            <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
              <circle class="checkmark-circle-bg" cx="26" cy="26" r="25" fill="none"/>
              <path class="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
            </svg>
          </div>
          <h3 class="modal-title-success">{{ successMessage }}</h3>
        </div>

        <div class="modal-body-success">
          <div v-if="successDetails.length > 0" class="success-details">
            <div
              v-for="(detail, index) in successDetails"
              :key="index"
              :class="['detail-item', detail.type === 'error' ? 'detail-error' : 'detail-success']"
            >
              <span v-if="detail.type === 'success'" class="detail-icon">✓</span>
              <span v-else class="detail-icon">✗</span>
              <span class="detail-text">{{ detail.text }}</span>
            </div>
          </div>
        </div>

        <div class="modal-footer-success">
          <button @click="showSuccessModal = false" class="btn-modal-ok">OK</button>
        </div>
      </div>
    </div>

    <!-- Modal Delete Report -->
    <div v-if="showDeleteModal" class="modal-overlay" @click.self="closeDeleteModal">
      <div class="modal-unlock-report">
        <div class="modal-header-danger">
          <div class="modal-icon-circle warning">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6M14 11v6"/>
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
            </svg>
          </div>
          <h3 class="modal-title">🗑️ Delete Report</h3>
        </div>

        <div class="modal-body">
          <div class="unlock-details">
            <div class="unlock-info-row">
              <span class="unlock-label">Property:</span>
              <span class="unlock-value">{{ selectedReport?.property_name }}</span>
            </div>
            <div class="unlock-info-row">
              <span class="unlock-label">Period:</span>
              <span class="unlock-value">{{ months.find(m => m.value === selectedMonth)?.label }} {{ selectedYear }}</span>
            </div>
          </div>

          <div class="warning-message-unlock">
            <p><strong>Are you sure you want to delete this draft report?</strong></p>
            <p class="warning-subtext">This action cannot be undone. The report data will be permanently removed.</p>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeDeleteModal" class="btn-modal-cancel">Cancel</button>
          <button @click="confirmDelete" class="btn-modal-delete" :disabled="deleting">
            <span v-if="!deleting">Delete</span>
            <span v-else>Deleting...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Unlock Report -->
    <div v-if="showUnlockModal" class="modal-overlay" @click.self="closeUnlockModal">
      <div class="modal-unlock-report">
        <div class="modal-header-warning">
          <div class="modal-icon-circle warning">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <h3 class="modal-title">⚠️ Unlock Report</h3>
        </div>

        <div class="modal-body">
          <div class="unlock-details">
            <div class="unlock-info-row">
              <span class="unlock-label">Property:</span>
              <span class="unlock-value">{{ selectedReport?.property_name }}</span>
            </div>
            <div class="unlock-info-row">
              <span class="unlock-label">Period:</span>
              <span class="unlock-value">{{ months.find(m => m.value === selectedMonth)?.label }} {{ selectedYear }}</span>
            </div>
          </div>

          <div class="warning-message-unlock">
            <p><strong>Are you sure you want to unlock this report?</strong></p>
            <p class="warning-subtext">The report will return to Draft status and you'll need to lock it again before sending.</p>
            <p v-if="selectedReport?.status === 'sent'" class="warning-subtext warning-extra">
              ⚠️ This report was already sent by email — unlocking does not recall the email already delivered to the owner.
            </p>
            <p v-if="selectedReport?.status === 'pushed_to_xero'" class="warning-subtext warning-extra">
              ⚠️ This report was already pushed to Xero — unlocking does not delete the invoice in Xero. You'll need to void it manually if needed.
            </p>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeUnlockModal" class="btn-modal-cancel">Cancel</button>
          <button @click="confirmUnlock" class="btn-modal-unlock" :disabled="unlocking">
            <span v-if="!unlocking">Unlock</span>
            <span v-else>Unlocking...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Push to Xero Confirmation -->
    <div v-if="showXeroConfirmModal" class="modal-overlay" @click.self="closeXeroConfirmModal">
      <div class="modal-send-report">
        <div class="modal-header-gradient">
          <div class="modal-icon-circle">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
          <h3 class="modal-title">Push to Xero</h3>
          <p class="modal-subtitle">{{ stats.sent }} invoice{{ stats.sent > 1 ? 's' : '' }} · {{ months.find(m => m.value === selectedMonth)?.label }} {{ selectedYear }}</p>
        </div>

        <div class="modal-body">
          <div class="warning-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <span>{{ stats.sent }} sent report{{ stats.sent > 1 ? 's' : '' }} will be pushed to Xero as DRAFT invoices for review.</span>
          </div>

          <div class="xero-test-mode-row">
            <div class="xero-test-mode-info">
              <div class="xero-test-mode-title">Test mode</div>
              <p class="xero-test-mode-help">When ON, no data is sent to Xero — you'll see exactly what would be pushed.</p>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" v-model="xeroTestMode" :disabled="previewLoading || pushing" />
              <span class="toggle-slider"></span>
            </label>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeXeroConfirmModal" class="btn-modal-cancel" :disabled="pushing || previewLoading">Cancel</button>
          <button @click="confirmXeroAction" class="btn-modal-send" :disabled="pushing || previewLoading">
            <span v-if="previewLoading">Loading preview...</span>
            <span v-else-if="pushing">Pushing...</span>
            <span v-else-if="xeroTestMode">Show preview</span>
            <span v-else>Push to Xero</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Xero Test Preview -->
    <div v-if="showXeroPreviewModal" class="modal-overlay" @click.self="closeXeroPreviewModal">
      <div class="modal-xero-preview">
        <div class="modal-header-gradient">
          <div class="modal-icon-circle">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <h3 class="modal-title">Xero Test Preview</h3>
          <p class="modal-subtitle">{{ xeroPreviewData?.count }} invoice{{ xeroPreviewData?.count > 1 ? 's' : '' }} — no data sent to Xero</p>
        </div>

        <div class="modal-body modal-body-scroll">
          <div
            v-for="(inv, idx) in xeroPreviewData?.invoices"
            :key="idx"
            class="xero-preview-invoice"
          >
            <div class="xero-preview-invoice-header">
              <div class="xero-preview-invoice-title">{{ inv.property_name }}</div>
              <div class="xero-preview-invoice-period">{{ inv.period }}</div>
            </div>

            <div class="xero-preview-meta-grid">
              <div class="xero-preview-meta-row">
                <span class="xero-preview-meta-label">Contact:</span>
                <span class="xero-preview-meta-value">
                  {{ inv.contact.name }}
                  <span v-if="inv.contact.email" class="xero-preview-meta-sub">&lt;{{ inv.contact.email }}&gt;</span>
                  <span v-if="!inv.contact.existing_in_xero" class="xero-preview-badge-new">will be created</span>
                </span>
              </div>
              <div class="xero-preview-meta-row">
                <span class="xero-preview-meta-label">Reference:</span>
                <span class="xero-preview-meta-value">{{ inv.reference }}</span>
              </div>
              <div class="xero-preview-meta-row">
                <span class="xero-preview-meta-label">Date / Due:</span>
                <span class="xero-preview-meta-value">{{ inv.date }} → {{ inv.due_date }}</span>
              </div>
              <div class="xero-preview-meta-row">
                <span class="xero-preview-meta-label">Currency:</span>
                <span class="xero-preview-meta-value">{{ inv.currency }}</span>
              </div>
              <div class="xero-preview-meta-row" v-if="inv.amounts_tax_exclusive">
                <span class="xero-preview-meta-label">Amounts are:</span>
                <span class="xero-preview-meta-value">Tax exclusive</span>
              </div>
            </div>

            <table class="xero-preview-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th class="num">Qty</th>
                  <th class="num">Price</th>
                  <th>Account</th>
                  <th>Tax rate</th>
                  <th class="num">Tax amount</th>
                  <th class="num">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(line, lidx) in inv.line_items" :key="lidx" :class="{ 'xero-preview-row-novat': line.tax_amount === 0 }">
                  <td>{{ line.description }}</td>
                  <td class="num">{{ line.quantity }}</td>
                  <td class="num">{{ line.unit_price.toFixed(2) }}</td>
                  <td>{{ line.account_code }} - {{ line.account_name }}</td>
                  <td>
                    <span v-if="line.tax_amount === 0" class="xero-preview-novat-badge">{{ line.tax_rate_label }}</span>
                    <span v-else>{{ line.tax_rate_label }}</span>
                  </td>
                  <td class="num">{{ line.tax_amount.toFixed(2) }}</td>
                  <td class="num">{{ line.line_total.toFixed(2) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="5" class="xero-preview-total-label">Subtotal</td>
                  <td></td>
                  <td class="num">{{ inv.totals.subtotal.toFixed(2) }}</td>
                </tr>
                <tr>
                  <td colspan="5" class="xero-preview-total-label">Total VAT</td>
                  <td></td>
                  <td class="num">{{ inv.totals.total_tax.toFixed(2) }}</td>
                </tr>
                <tr class="xero-preview-grand-total">
                  <td colspan="5" class="xero-preview-total-label">Total</td>
                  <td></td>
                  <td class="num">{{ inv.totals.total.toFixed(2) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeXeroPreviewModal" class="btn-modal-ok">Close</button>
        </div>
      </div>
    </div>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';
import Topbar from '@/components/Topbar.vue';

export default {
  name: 'MonthEndView',
  components: {
    Sidebar,
    Topbar
  },
  data() {
    return {
      selectedMonth: new Date().getMonth() + 1,
      selectedYear: new Date().getFullYear(),
      currentFilter: 'all',
      reports: [],
      stats: {
        total: 0,
        ready_to_send: 0,
        sent: 0,
        pushed_to_xero: 0,
        progress_percent: 0
      },
      months: [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' }
      ],
      years: [],
      loading: false,
      sending: false,
      pushing: false,
      unlocking: false,
      deleting: false,
      showSendModal: false,
      showUnlockModal: false,
      showDeleteModal: false,
      selectedReport: null,
      showSuccessModal: false,
      successMessage: '',
      successDetails: [],
      showXeroConfirmModal: false,
      showXeroPreviewModal: false,
      xeroTestMode: false,
      xeroPreviewData: null,
      previewLoading: false
    };
  },

  computed: {
    baseUrl() {
      // Use same logic as settings.js for consistency
      if (window.location.pathname.startsWith('/dev')) {
        return window.location.origin + '/dev';
      }
      return window.location.origin;
    },
    reviewedCount() {
      return (this.stats.ready_to_send || 0) + (this.stats.sent || 0);
    },
    reviewedPercent() {
      const total = this.stats.total || 0;
      return total === 0 ? 0 : Math.round((this.reviewedCount / total) * 100);
    },
    sentPercent() {
      const total = this.stats.total || 0;
      return total === 0 ? 0 : Math.round(((this.stats.sent || 0) / total) * 100);
    },
    readyToSendCount() {
      return this.stats.ready_to_send || 0;
    },
    needsReviewCount() {
      return this.stats.needs_action || 0;
    },
    isComplete() {
      return (this.stats.total || 0) > 0 && (this.stats.sent || 0) === this.stats.total;
    },
    activeStage() {
      if (this.isComplete) return 3;
      if (this.needsReviewCount > 0) return 1;
      if ((this.stats.sent || 0) < (this.stats.total || 0)) return 2;
      return 1;
    },
    subtitleText() {
      const parts = [];
      if (this.readyToSendCount > 0) {
        parts.push(`${this.readyToSendCount} reviewed but not yet sent`);
      }
      if (this.needsReviewCount > 0) {
        parts.push(`${this.needsReviewCount} still need reviewing`);
      }
      if (parts.length === 0 && (this.stats.total || 0) > 0) {
        return 'All reports sent and delivered.';
      }
      if (parts.length === 0) {
        return 'No reports yet for this month.';
      }
      return parts.join(' · ');
    }
  },

  methods: {
    async fetchReports() {
      this.loading = true;
      try {
        const token = localStorage.getItem('token');

        const response = await axios.get(
          `${this.baseUrl}/api/pms/month-end/`,
          {
            params: {
              year: this.selectedYear,
              month: this.selectedMonth,
              status: this.currentFilter
            },
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (response.data.success) {
          this.reports = response.data.data.reports;
          this.stats = response.data.data.stats;
        }
      } catch (error) {
        console.error('Error fetching reports:', error);
      } finally {
        this.loading = false;
      }
    },

    async autoSelectMonth() {
      const now = new Date();
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const prevMonth = prev.getMonth() + 1;
      const prevYear = prev.getFullYear();

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${this.baseUrl}/api/pms/month-end/`,
          {
            params: { year: prevYear, month: prevMonth, status: 'all' },
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        if (response.data && response.data.success) {
          const s = response.data.data.stats;
          if (s.total > 0 && s.sent < s.total) {
            this.selectedMonth = prevMonth;
            this.selectedYear = prevYear;
          }
        }
      } catch (e) {
        console.warn('autoSelectMonth failed, defaulting to current month:', e);
      }
    },

    changeFilter(filter) {
      this.currentFilter = filter;
      this.fetchReports();
    },

    openSendModal(report) {
      this.selectedReport = report;
      this.showSendModal = true;
    },

    closeSendModal() {
      this.showSendModal = false;
      this.selectedReport = null;
    },

    getInitials(name) {
      if (!name) return 'NA';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    async confirmSendReport() {
      if (!this.selectedReport) return;

      this.sending = true;
      try {
        const token = localStorage.getItem('token');

        const response = await axios.post(
          `${this.baseUrl}/api/pms/monthly-reports/${this.selectedReport.id}/send/`,
          {},
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          alert(`Report sent to ${this.selectedReport.owner_email}`);
          this.closeSendModal();
          this.fetchReports();
        }
      } catch (error) {
        console.error('Error sending report:', error);
        const message = error.response?.data?.message || 'Error sending report';
        alert(message);
      } finally {
        this.sending = false;
      }
    },

    openUnlockModal(report) {
      this.selectedReport = report;
      this.showUnlockModal = true;
    },

    closeUnlockModal() {
      this.showUnlockModal = false;
      this.selectedReport = null;
    },

    openDeleteModal(report) {
      this.selectedReport = report;
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.selectedReport = null;
    },

    async confirmDelete() {
      if (!this.selectedReport) return;

      this.deleting = true;
      try {
        const token = localStorage.getItem('token');

        const response = await axios.delete(
          `${this.baseUrl}/api/pms/monthly-reports/${this.selectedReport.id}/delete/`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          this.successMessage = 'Report deleted successfully!';
          this.successDetails = [{
            type: 'success',
            text: `${this.selectedReport.property_name} has been removed`
          }];
          this.showSuccessModal = true;

          this.closeDeleteModal();
          this.fetchReports();
        }
      } catch (error) {
        console.error('Error deleting report:', error);
        const message = error.response?.data?.message || 'Error deleting report';
        alert(message);
      } finally {
        this.deleting = false;
      }
    },

    async confirmUnlock() {
      if (!this.selectedReport) return;

      this.unlocking = true;
      try {
        const token = localStorage.getItem('token');

        const response = await axios.post(
          `${this.baseUrl}/api/pms/unlock-report/`,
          { report_id: this.selectedReport.id },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          // Show success message
          this.successMessage = 'Report unlocked successfully!';
          this.successDetails = [{
            type: 'success',
            text: `${this.selectedReport.property_name} returned to Draft status`
          }];
          this.showSuccessModal = true;

          // Close unlock modal and refresh
          this.closeUnlockModal();
          this.fetchReports();
        }
      } catch (error) {
        console.error('Error unlocking report:', error);
        const message = error.response?.data?.message || 'Error unlocking report';
        alert(message);
      } finally {
        this.unlocking = false;
      }
    },

    async sendReport(report) {
      if (!confirm(`Send report to ${report.owner_name}?\n\nEmail: ${report.owner_email}`)) {
        return;
      }

      this.sending = true;
      try {
        const token = localStorage.getItem('token');

        const response = await axios.post(
          `${this.baseUrl}/api/pms/monthly-reports/${report.id}/send/`,
          {},
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          alert(`Report sent to ${report.owner_email}`);
          this.fetchReports();
        }
      } catch (error) {
        console.error('Error sending report:', error);
        const message = error.response?.data?.message || 'Error sending report';
        alert(message);
      } finally {
        this.sending = false;
      }
    },

    async sendAllUnsent() {
      if (!confirm(`Send ${this.stats.ready_to_send} unsent reports?\n\nYou will confirm each one individually.`)) {
        return;
      }

      this.sending = true;
      try {
        const token = localStorage.getItem('token');

        const response = await axios.post(
          `${this.baseUrl}/api/pms/send-all-unsent/`,
          {
            year: this.selectedYear,
            month: this.selectedMonth
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          const unsentReports = response.data.data.unsent_reports;

          if (unsentReports.length === 0) {
            alert('No unsent reports');
            this.sending = false;
            return;
          }

          let sentCount = 0;
          for (const report of unsentReports) {
            const confirmed = confirm(
              `Send report to:\n\n` +
              `Property: ${report.property_name}\n` +
              `Owner: ${report.owner_name}\n` +
              `Email: ${report.owner_email}\n\n` +
              `Continue? (${sentCount + 1}/${unsentReports.length})`
            );

            if (confirmed) {
              try {
                await axios.post(
                  `${this.baseUrl}/api/pms/monthly-reports/${report.id}/send/`,
                  {},
                  { headers: { 'Authorization': `Bearer ${token}` } }
                );
                sentCount++;
              } catch (err) {
                console.error(`Error sending report ${report.id}:`, err);
                alert(`Error sending to ${report.owner_name}: ${err.response?.data?.message || err.message}`);
              }
            }
          }

          alert(`${sentCount} reports sent successfully`);
          this.fetchReports();
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Error processing reports');
      } finally {
        this.sending = false;
      }
    },

    openXeroConfirmModal() {
      if (this.stats.sent === 0) return;
      this.xeroTestMode = false;
      this.showXeroConfirmModal = true;
    },

    closeXeroConfirmModal() {
      if (this.pushing || this.previewLoading) return;
      this.showXeroConfirmModal = false;
    },

    closeXeroPreviewModal() {
      this.showXeroPreviewModal = false;
      this.xeroPreviewData = null;
    },

    async confirmXeroAction() {
      if (this.xeroTestMode) {
        await this.fetchXeroPreview();
      } else {
        await this.pushToXero();
      }
    },

    async fetchXeroPreview() {
      this.previewLoading = true;
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${this.baseUrl}/api/pms/xero-preview/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            year: this.selectedYear,
            month: this.selectedMonth
          })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || 'Failed to build preview');
        }

        this.xeroPreviewData = data.data;
        this.showXeroConfirmModal = false;
        this.showXeroPreviewModal = true;
      } catch (error) {
        console.error('Error fetching preview:', error);
        this.successMessage = 'Failed to build Xero preview';
        this.successDetails = [{ type: 'error', text: error.message }];
        this.showXeroConfirmModal = false;
        this.showSuccessModal = true;
      } finally {
        this.previewLoading = false;
      }
    },

    async pushToXero() {
      if (this.stats.sent === 0) return;

      this.showXeroConfirmModal = false;
      this.pushing = true;

      try {
        const token = localStorage.getItem('token');

        const response = await fetch(`${this.baseUrl}/api/pms/push-to-xero/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            year: this.selectedYear,
            month: this.selectedMonth
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to push to Xero');
        }

        if (data.success) {
          const { pushed, failed, results, errors } = data.data;

          this.successMessage = `Successfully pushed ${pushed} invoice${pushed > 1 ? 's' : ''} to Xero`;
          this.successDetails = [];

          if (results && results.length > 0) {
            this.successDetails = results.map(r => ({
              type: 'success',
              text: `${r.property_name}: ${r.invoice_number}`
            }));
          }

          if (failed > 0 && errors && errors.length > 0) {
            errors.forEach(err => {
              this.successDetails.push({
                type: 'error',
                text: `${err.property_name}: ${err.error}`
              });
            });
          }

          this.showSuccessModal = true;
          this.fetchReports();
        } else {
          throw new Error(data.message || 'Unknown error');
        }

      } catch (error) {
        console.error('Error pushing to Xero:', error);

        this.successMessage = 'Failed to push to Xero';
        this.successDetails = [{
          type: 'error',
          text: error.message
        }];
        this.showSuccessModal = true;

        if (error.message.includes('not connected') || error.message.includes('expired')) {
          setTimeout(() => {
            this.$router.push('/xero-settings');
          }, 2000);
        }
      } finally {
        this.pushing = false;
      }
    },

    getStatusBadgeClass(status) {
      const classes = {
        'ready_to_send': 'badge-to-send',
        'sent': 'badge-sent',
        'pushed_to_xero': 'badge-sent'
      };
      return classes[status] || 'badge-default';
    },

    getStatusClass(status) {
      const classes = {
        'ready_to_send': 'amber',
        'sent': 'green',
        'pushed_to_xero': 'blue'
      };
      return classes[status] || 'gray';
    },

    getStatusLabel(status) {
      const labels = {
        'ready_to_send': 'To send',
        'sent': 'Sent',
        'pushed_to_xero': 'Sent'
      };
      return labels[status] || status;
    },

    formatGBP(value) {
      const n = parseFloat(value || 0);
      return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    goToEmailContacts() {
      this.$router.push({ path: '/reservations', query: { openContacts: 'true' } });
    },

    logout() {
      localStorage.removeItem('token');
      this.$router.push('/login');
    }
  },

  async mounted() {
    const currentYear = new Date().getFullYear();
    this.years = [
      currentYear - 2,
      currentYear - 1,
      currentYear,
      currentYear + 1
    ];

    if (this.$route.query.month) {
      this.selectedMonth = parseInt(this.$route.query.month);
      if (this.$route.query.year) {
        this.selectedYear = parseInt(this.$route.query.year);
      }
    } else {
      await this.autoSelectMonth();
    }

    this.fetchReports();
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.month-end-container {
  display: flex;
  min-height: 100vh;
  background: #e6e4f7;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ============= SIDEBAR ============= */
/* Sidebar styles removed - using component Sidebar.vue */

/* ============= MAIN ============= */
.main {
  margin-left: 240px;
  flex: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.period-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 18px;
}

/* ============= CONTENT ============= */
.content {
  padding: 24px 28px;
  flex: 1;
}

/* ============= PERIOD SELECTOR ============= */
.period-select {
  padding: 8px 14px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  background: white;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.period-select:hover {
  border-color: #8075f0;
}

.period-select:focus {
  outline: none;
  border-color: #8075f0;
  box-shadow: 0 0 0 3px rgba(128, 117, 240, 0.1);
}

/* ============= PROGRESS CARD AVEC STATS ============= */
.progress-card-modern {
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(128, 117, 240, 0.08);
}

.progress-header {
  margin-bottom: 16px;
}

.progress-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  font-family: 'Bricolage Grotesque', sans-serif;
}

.progress-content {
  display: flex;
  align-items: center;
  gap: 40px;
}

.progress-left {
  flex: 1;
}

.progress-bar-wrapper {
  margin-bottom: 10px;
}

.progress-bar-track {
  height: 8px;
  background: #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #8075f0 0%, #9b8ff5 100%);
  border-radius: 6px;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.progress-info-text {
  font-size: 12px;
  color: #9ca3af;
  font-weight: 500;
}

.progress-right {
  display: flex;
  gap: 32px;
  align-items: center;
  justify-content: flex-end;
}

.stat-item {
  text-align: center;
  min-width: 50px;
}

.stat-number {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 32px;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 10px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: capitalize;
  letter-spacing: 0;
}

.stat-to-send .stat-number {
  color: #f59e0b;
}

.stat-sent .stat-number {
  color: #10b981;
}

.stat-total .stat-number {
  color: #9ca3af;
}

/* ============= FILTERS + ACTIONS SUR MÊME LIGNE ============= */
.filters-actions-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  gap: 20px;
}

/* ============= FILTERS PILLS ============= */
.filters-pills {
  display: flex;
  gap: 8px;
}

.pill-btn {
  padding: 6px 16px;
  border: 1.5px solid #e5e7eb;
  background: white;
  border-radius: 50px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.pill-btn:hover {
  border-color: #8075f0;
  color: #8075f0;
}

.pill-btn.active {
  background: #8075f0;
  color: white;
  border-color: #8075f0;
}

/* ============= ACTION BUTTONS ============= */
.action-buttons-row {
  display: flex;
  gap: 8px;
}

.btn-send-all {
  background: #fbbf24;
  color: #78350f;
  border: none;
  padding: 7px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.btn-send-all:hover:not(:disabled) {
  background: #f59e0b;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(251, 191, 36, 0.3);
}

.btn-send-all:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-push-xero {
  background: #8075f0;
  color: white;
  border: none;
  padding: 7px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.btn-push-xero:hover:not(:disabled) {
  background: #6f63e6;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(128, 117, 240, 0.3);
}

.btn-push-xero:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ============= TABLE CARD ============= */
.table-card-modern {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(128, 117, 240, 0.08);
  overflow-x: auto;
}

.reports-table-modern {
  width: 100%;
  border-collapse: collapse;
}

.reports-table-modern thead th {
  text-align: left;
  padding: 12px 12px 12px 0;
  font-size: 10px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  border-bottom: 1.5px solid #f3f4f6;
  font-family: 'DM Sans', sans-serif;
}

.reports-table-modern tbody td {
  padding: 14px 12px 14px 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 14px;
  color: #1f2937;
}

.property-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.property-name {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
}

.owner-name-gray {
  font-size: 12px;
  color: #9ca3af;
  font-weight: 500;
}

.owner-name-cell {
  font-size: 13px;
  color: #4b5563;
  font-weight: 500;
}

.settlement-amount {
  font-weight: 700;
  color: #111827;
  font-size: 14px;
}

.commission-amount {
  font-weight: 600;
  color: #4b5563;
  font-size: 14px;
}

.reports-table-modern {
  table-layout: fixed;
}

.reports-table-modern th.th-property { width: 28%; }
.reports-table-modern th.th-right    { width: 13%; text-align: left; padding-left: 0; }
.reports-table-modern td.td-right    { text-align: left; padding-left: 0; }
.reports-table-modern th.th-status   { width: 14%; text-align: left; padding-left: 12px; }
.reports-table-modern td.td-status   { text-align: left; padding-left: 12px; white-space: nowrap; }
.reports-table-modern th.th-action   { width: 16%; text-align: center; }
.reports-table-modern td.td-action   { text-align: center; white-space: nowrap; }

.reports-table-modern td.td-action .action-buttons {
  justify-content: center;
}

.reports-table-modern th.th-unlock,
.reports-table-modern td.td-unlock {
  text-align: center;
  width: 60px;
}

.btn-unlock-icon {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 6px;
  transition: all 0.15s;
  color: #9ca3af;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-unlock-icon:hover:not(:disabled) {
  background: #f3f4f6;
  color: #4b5563;
}

.btn-unlock-icon:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.fee-amount {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
}

.cleaning-amount {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.inc-vat-text {
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
}

.total-ex-vat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.total-main {
  font-weight: 700;
  color: #111827;
  font-size: 14px;
}

.vat-addition {
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
}

/* ============= STATUS BADGES ============= */
.status-badge {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  border: 1.5px solid;
}

.badge-to-send {
  background: #fffbeb;
  color: #d97706;
  border-color: #fde68a;
}

.badge-sent {
  background: #ecfdf5;
  color: #059669;
  border-color: #a7f3d0;
}

.badge-default {
  background: #f3f4f6;
  color: #6b7280;
  border-color: #e5e7eb;
}

/* ============= ACTION BUTTONS (table) ============= */
.btn-send-action {
  background: #8075f0;
  color: white;
  border: none;
  padding: 6px 16px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.btn-send-action:hover:not(:disabled) {
  background: #6f63e6;
  transform: translateY(-1px);
}

.btn-send-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn-unlock-action {
  background: #f59e0b;
  color: white;
  border: none;
  padding: 6px 14px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.btn-unlock-action:hover:not(:disabled) {
  background: #d97706;
  transform: translateY(-1px);
}

.btn-unlock-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-review-action {
  background: white;
  color: #4b5563;
  border: 1.5px solid #e5e7eb;
  padding: 6px 16px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.btn-review-action:hover {
  border-color: #8075f0;
  color: #8075f0;
  transform: translateY(-1px);
}

.btn-delete-action {
  background: #ef4444;
  color: white;
  border: none;
  padding: 6px 14px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.btn-delete-action:hover:not(:disabled) {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-delete-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.checkmark-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: #10b981;
  color: white;
  border-radius: 50%;
  font-weight: 700;
  font-size: 14px;
}

/* ============= LOADING & EMPTY ============= */
.loading-state {
  text-align: center;
  padding: 80px 20px;
  color: #6b7280;
}

.spinner {
  border: 3px solid #f3f4f6;
  border-top: 3px solid #8075f0;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
}

.link-primary {
  display: inline-block;
  margin-top: 16px;
  color: #8075f0;
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
}

.link-primary:hover {
  text-decoration: underline;
}

/* ============= MODAL SEND REPORT ============= */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-send-report {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 440px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}

.modal-header-gradient {
  background: linear-gradient(135deg, #8075f0 0%, #9b8ff5 100%);
  padding: 28px 24px;
  text-align: center;
  color: white;
}

.modal-icon-circle {
  width: 52px;
  height: 52px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
}

.modal-icon-circle svg {
  width: 20px;
  height: 20px;
}

.modal-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 6px 0;
}

.modal-subtitle {
  font-size: 13px;
  margin: 0;
  opacity: 0.9;
  font-weight: 500;
}

.modal-body {
  padding: 24px;
}

.sending-to-section {
  margin-bottom: 18px;
}

.sending-to-label {
  font-size: 10px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 10px;
}

.recipient-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 10px;
}

.avatar-circle {
  width: 40px;
  height: 40px;
  background: #8075f0;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
}

.recipient-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.recipient-name {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
}

.recipient-email {
  font-size: 13px;
  color: #6b7280;
}

.warning-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: #fffbeb;
  border: 1.5px solid #fde68a;
  border-radius: 10px;
  font-size: 13px;
  color: #92400e;
  font-weight: 500;
}

.warning-message svg {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
}

.modal-footer {
  padding: 18px 24px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.btn-modal-cancel {
  padding: 10px 20px;
  border: 1.5px solid #e5e7eb;
  background: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: #4b5563;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.btn-modal-cancel:hover {
  border-color: #d1d5db;
  background: #f9fafb;
}

.btn-modal-send {
  padding: 10px 24px;
  border: none;
  background: #8075f0;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
}

.btn-modal-send:hover:not(:disabled) {
  background: #6f63e6;
  transform: translateY(-1px);
}

.btn-modal-send:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ============= RESPONSIVE ============= */
@media (max-width: 1024px) {
  .content {
    padding: 20px;
  }

  .page-title {
    font-size: 18px;
  }

  .progress-content {
    gap: 24px;
  }

  .progress-right {
    gap: 24px;
  }

  .stat-number {
    font-size: 36px;
  }
}

@media (max-width: 768px) {
  .main {
    margin-left: 0;
  }

  .sidebar {
    transform: translateX(-100%);
  }

  .topbar {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    height: auto;
    padding: 16px 20px;
  }

  .progress-content {
    flex-direction: column;
    gap: 20px;
  }

  .progress-left {
    max-width: 100%;
  }

  .progress-right {
    width: 100%;
    justify-content: space-around;
  }

  .filters-actions-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filters-pills {
    justify-content: center;
  }

  .action-buttons-row {
    flex-direction: column;
  }

  .table-card-modern {
    padding: 16px;
    overflow-x: scroll;
  }
}

/* ============= MODAL SUCCESS XERO ============= */
.modal-success {
  background: white;
  border-radius: 16px;
  max-width: 520px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  animation: slideUp 0.3s ease-out;
}

.modal-header-success {
  padding: 40px 32px 24px;
  text-align: center;
  border-bottom: 1px solid #f3f4f6;
}

.checkmark-circle {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  position: relative;
}

.checkmark {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: block;
  stroke-width: 3;
  stroke: #10b981;
  stroke-miterlimit: 10;
  box-shadow: inset 0px 0px 0px #10b981;
  animation: fill 0.4s ease-in-out 0.4s forwards, scale 0.3s ease-in-out 0.9s both;
}

.checkmark-circle-bg {
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  stroke-width: 3;
  stroke-miterlimit: 10;
  stroke: #10b981;
  fill: none;
  animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}

.checkmark-check {
  transform-origin: 50% 50%;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  stroke: #10b981;
  stroke-width: 3;
  animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
}

@keyframes stroke {
  100% {
    stroke-dashoffset: 0;
  }
}

@keyframes scale {
  0%, 100% {
    transform: none;
  }
  50% {
    transform: scale3d(1.1, 1.1, 1);
  }
}

@keyframes fill {
  100% {
    box-shadow: inset 0px 0px 0px 30px #10b981;
  }
}

.modal-title-success {
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  font-family: 'DM Sans', sans-serif;
}

.modal-body-success {
  padding: 24px 32px;
  max-height: 400px;
  overflow-y: auto;
}

.success-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #f9fafb;
  font-size: 14px;
}

.detail-success {
  background: #ecfdf5;
  border-left: 3px solid #10b981;
}

.detail-error {
  background: #fef2f2;
  border-left: 3px solid #ef4444;
}

.detail-icon {
  font-weight: bold;
  font-size: 16px;
  flex-shrink: 0;
}

.detail-success .detail-icon {
  color: #10b981;
}

.detail-error .detail-icon {
  color: #ef4444;
}

.detail-text {
  color: #4b5563;
  line-height: 1.5;
  font-family: 'DM Sans', sans-serif;
}

.modal-footer-success {
  padding: 20px 32px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  justify-content: center;
}

.btn-modal-ok {
  padding: 12px 32px;
  border: none;
  background: #8075f0;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'DM Sans', sans-serif;
  min-width: 120px;
}

.btn-modal-ok:hover {
  background: #6d61e0;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(128, 117, 240, 0.3);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ============= UNLOCK MODAL ============= */
.modal-unlock-report {
  background: white;
  border-radius: 16px;
  max-width: 480px;
  width: 90%;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: slideUp 0.3s ease-out;
}

.modal-header-warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  padding: 28px 28px 24px;
  text-align: center;
  position: relative;
}

.modal-icon-circle.warning {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  backdrop-filter: blur(10px);
}

.unlock-details {
  background: #f9fafb;
  padding: 16px;
  border-radius: 10px;
  margin-bottom: 20px;
}

.unlock-info-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
}

.unlock-info-row:not(:last-child) {
  border-bottom: 1px solid #e5e7eb;
}

.unlock-label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 600;
}

.unlock-value {
  font-size: 14px;
  color: #1f2937;
  font-weight: 600;
}

.warning-message-unlock {
  text-align: center;
  padding: 0 12px;
}

.warning-message-unlock p {
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
  margin-bottom: 8px;
}

.warning-message-unlock strong {
  font-weight: 700;
  color: #111827;
}

.warning-subtext {
  font-size: 13px !important;
  color: #6b7280 !important;
}

.warning-extra {
  margin-top: 8px !important;
  padding: 8px 10px !important;
  background: #fef3c7 !important;
  border-left: 3px solid #f59e0b !important;
  border-radius: 4px !important;
  color: #92400e !important;
}

.btn-modal-unlock {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border: none;
  padding: 11px 26px;
  border-radius: 9px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
  font-family: 'DM Sans', sans-serif;
}

.btn-modal-unlock:hover:not(:disabled) {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
}

.btn-modal-unlock:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* ============= DELETE MODAL ============= */
.modal-header-danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  padding: 28px 28px 24px;
  text-align: center;
  position: relative;
  color: white;
}

.btn-modal-delete {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  padding: 11px 26px;
  border-radius: 9px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
  font-family: 'DM Sans', sans-serif;
}

.btn-modal-delete:hover:not(:disabled) {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

.btn-modal-delete:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Xero Confirm Modal — Test mode toggle row */
.xero-test-mode-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
  padding: 14px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.xero-test-mode-info {
  flex: 1;
}

.xero-test-mode-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

.xero-test-mode-help {
  font-size: 12px;
  color: #6b7280;
  margin: 4px 0 0 0;
  font-style: italic;
  line-height: 1.4;
}

/* iOS-style toggle switch (same pattern as SettingsView) */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
  flex-shrink: 0;
}

.toggle-switch input[type="checkbox"] {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e0;
  transition: 0.3s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: #667eea;
}

.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(24px);
}

.toggle-switch input:focus + .toggle-slider {
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
}

.toggle-switch input:disabled + .toggle-slider {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Xero Preview Modal — wider modal + table */
.modal-xero-preview {
  background: white;
  border-radius: 16px;
  max-width: 1100px;
  width: 95%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.modal-body-scroll {
  overflow-y: auto;
  flex: 1;
}

.xero-preview-invoice {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fafbfc;
}

.xero-preview-invoice:last-child {
  margin-bottom: 0;
}

.xero-preview-invoice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e5e7eb;
}

.xero-preview-invoice-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.xero-preview-invoice-period {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.xero-preview-meta-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
  margin-bottom: 14px;
}

.xero-preview-meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.xero-preview-meta-label {
  color: #6b7280;
  font-weight: 500;
  min-width: 110px;
}

.xero-preview-meta-value {
  color: #111827;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.xero-preview-meta-sub {
  color: #6b7280;
  font-weight: 400;
}

.xero-preview-badge-new {
  background: #fef3c7;
  color: #92400e;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.xero-preview-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  font-size: 13px;
}

.xero-preview-table th {
  background: #f3f4f6;
  text-align: left;
  padding: 10px 12px;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.xero-preview-table th.num,
.xero-preview-table td.num {
  text-align: right;
}

.xero-preview-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
  color: #111827;
}

.xero-preview-table tbody tr:last-child td {
  border-bottom: 1px solid #e5e7eb;
}

.xero-preview-table tfoot td {
  background: #f9fafb;
  padding: 8px 12px;
  font-size: 13px;
  border-bottom: 1px solid #f3f4f6;
}

.xero-preview-total-label {
  text-align: right;
  font-weight: 600;
  color: #374151;
}

.xero-preview-table tfoot tr.xero-preview-grand-total td {
  background: #eef2ff;
  font-weight: 700;
  color: #1e3a8a;
  font-size: 14px;
  border-bottom: none;
}

.xero-preview-novat-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.xero-preview-row-novat td {
  background: #fafafa;
}

/* ============================================================
   W1 Refonte #8 — Month-End progress (3 stages)
   ============================================================ */
.progress-card-v2 {
  background: #fff;
  border-radius: 14px;
  padding: 22px 24px 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(20, 14, 70, 0.04);
  border: 1px solid #eceaf6;
  font-family: 'DM Sans', system-ui, sans-serif;
}

.progress-header-v2 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.progress-title-v2 {
  font-size: 17px;
  font-weight: 700;
  color: #14143a;
  letter-spacing: -0.01em;
  font-family: 'Bricolage Grotesque', 'DM Sans', sans-serif;
}

.properties-managed {
  font-size: 13px;
  color: #8b8aaa;
  font-weight: 500;
}

.stages-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 22px;
}

.stage-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 12px;
  border: 1.5px solid #d9d6ea;
  background: #fff;
  box-shadow: 0 2px 6px rgba(20, 14, 70, 0.06), 0 1px 2px rgba(20, 14, 70, 0.04);
  transition: all 0.15s ease;
}

.stage-card:hover {
  box-shadow: 0 4px 12px rgba(20, 14, 70, 0.08), 0 2px 4px rgba(20, 14, 70, 0.05);
  transform: translateY(-1px);
}

.stage-card.active {
  background: #f3f0fe;
  border-color: #332999;
  box-shadow: 0 3px 10px rgba(51, 41, 153, 0.15), 0 1px 3px rgba(51, 41, 153, 0.08);
}

.stage-card.done {
  border-color: #1fa385;
  box-shadow: 0 3px 10px rgba(31, 163, 133, 0.15), 0 1px 3px rgba(31, 163, 133, 0.08);
}

.stage-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  background: #e9e6f5;
  color: #8b8aaa;
  flex-shrink: 0;
}

.stage-card.active .stage-circle {
  background: #332999;
  color: #fff;
}

.stage-card.done .stage-circle {
  background: #1fa385;
  color: #fff;
}

.stage-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.stage-name {
  font-size: 14px;
  font-weight: 700;
  color: #14143a;
  line-height: 1.2;
}

.stage-detail {
  font-size: 12px;
  color: #6b6a8a;
  line-height: 1.3;
}

.progress-bars-stack {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.progress-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
}

.progress-row-label {
  font-size: 12px;
  color: #6b6a8a;
  font-weight: 500;
}

.progress-row-count {
  font-size: 12px;
  color: #14143a;
  font-weight: 600;
}

.progress-bar-v2 {
  width: 100%;
  height: 6px;
  background: #eceaf6;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 4px;
}

.progress-bar-v2-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.bar-violet .progress-bar-v2-fill {
  background: #332999;
}

.bar-green .progress-bar-v2-fill {
  background: #1fa385;
}

.progress-subtitle {
  font-size: 12px;
  color: #8b8aaa;
  margin-top: 4px;
}

.ready-banner {
  background: #f3f0fe;
  border: 1px solid #d9d2f9;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.ready-banner-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ready-banner-title {
  font-size: 14px;
  font-weight: 700;
  color: #14143a;
}

.ready-banner-sub {
  font-size: 12px;
  color: #6b6a8a;
}

.btn-send-all-ready {
  background: #332999;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;
  white-space: nowrap;
}

.btn-send-all-ready:hover:not(:disabled) {
  background: #251f7a;
}

.btn-send-all-ready:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.xero-action-row {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 14px;
}

.properties-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eceaf6;
}

.properties-title {
  font-size: 15px;
  font-weight: 700;
  color: #14143a;
  font-family: 'Bricolage Grotesque', 'DM Sans', sans-serif;
}

.filters-pills-v2 {
  display: flex;
  gap: 6px;
}

.pill-btn-v2 {
  background: transparent;
  color: #6b6a8a;
  border: 1px solid #d9d6ea;
  border-radius: 18px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}

.pill-btn-v2:hover {
  border-color: #332999;
  color: #332999;
}

.pill-btn-v2.active {
  background: #332999;
  color: #fff;
  border-color: #332999;
}
</style>
